// src/components/gavion/privacy/api/types.ts
import type { DataProvider } from "ra-core";
import type { ErasureReason } from "@/components/gavion/domain/privacy";

export type PrivacySettings = {
  officerName: string;
  officerEmail: string;
  recordingDays: number;
  transcriptDays: number;
  auditDays: number;
};
export type PersonMatch = {
  companyId: number;
  companyName: string;
  phone: string | null;
  email: string | null;
  contacts: number;
  /** Full name of the first contact that matched the query, or null when only the company matched. */
  matchedContact: string | null;
};
export type ExportResult = {
  url: string;
  expiresAt: string;
  files: number;
  missing: number;
  bytes: number;
};
export type ErasureResult = {
  counts: Record<string, number>;
  purgeQueued: number;
  /** Columns that still hold one of the person's values; null = the residual scan failed (UNKNOWN, never clean). */
  residual: Array<{ table: string; column: string; hits: number }> | null;
};
export type AuditRow = {
  id: number;
  at: string;
  action: string;
  actorName: string | null;
  entityType: string | null;
  entityId: string | null;
};
export type AuditQuery = { page: number; perPage: number; action?: string };
export type PrivacyMethods = {
  privacyGetSettings(): Promise<PrivacySettings>;
  privacySaveSettings(s: PrivacySettings): Promise<void>;
  privacySearchPeople(q: string): Promise<PersonMatch[]>;
  privacyExportPerson(companyId: number): Promise<ExportResult>;
  privacyErasePerson(
    companyId: number,
    reason: ErasureReason,
    confirmName: string,
  ): Promise<ErasureResult>;
  privacyListAudit(q: AuditQuery): Promise<{ rows: AuditRow[]; total: number }>;
};
export type PrivacyDataProvider = DataProvider & PrivacyMethods;

export class PrivacyApiError extends Error {
  constructor(readonly code: string) {
    super(code);
    this.name = "PrivacyApiError";
  }
}
/** fetch() rejections (Chrome, Firefox, Safari) and the supabase-js wrappers that carry them. */
const NETWORK_MESSAGE =
  /failed to fetch|networkerror|load failed|network request failed/i;

/**
 * Stable code of a PrivacyApiError or a P0001 RPC error ({message:'forbidden'}); a transport
 * failure (FunctionsFetchError, or a fetch rejection message) → "network"; anything else, including a
 * TypeError from a programming bug → "unknown".
 */
export const privacyErrorCode = (e: unknown): string => {
  if (e instanceof PrivacyApiError) return e.code;
  const o =
    typeof e === "object" && e !== null ? (e as Record<string, unknown>) : {};
  if (o.name === "FunctionsFetchError") return "network";
  const m = o.message;
  if (typeof m === "string" && NETWORK_MESSAGE.test(m)) return "network";
  return typeof m === "string" && /^[a-z_]{3,40}$/.test(m) ? m : "unknown";
};
/** Audit actions listed in the viewer's filter (spec 11 §8; others still display, unfiltered). */
export const AUDIT_ACTIONS = [
  "erasure.person",
  "erasure.residual",
  "export.person",
  "settings.update",
  "recording.play",
  "user.invite",
  "user.role_change",
  "user.disable",
  "suppression.remove",
  "api.key_create",
  "api.key_revoke",
  "campaign.schedule",
  "campaign.cancel",
  "export.report",
] as const;
